import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {number | undefined} */
  #confettiHideTimeout;

  /** @type {{x: number, y: number} | null} */
  #lastPointerPosition = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    // Capture phase so this always runs, no matter which button/form handles (or stops) the click.
    document.addEventListener('pointerdown', this.#recordPointerPosition, { capture: true });
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener('pointerdown', this.#recordPointerPosition, { capture: true });
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
  }

  /**
   * Remembers where the visitor last pressed down, so a confetti burst
   * triggered right after (by a successful add-to-cart) can originate from
   * whichever "Ajouter au panier" button they actually pressed — main
   * product form, quick add, or any of the carousels — without having to
   * special-case every one of them.
   * @param {PointerEvent} event
   */
  #recordPointerPosition = (event) => {
    this.#lastPointerPosition = { x: event.clientX, y: event.clientY };
  };

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open')) {
      // Si un autre <dialog> natif (ex: la modale "quick add" ouverte depuis une
      // carte produit) est encore modal à cet instant, on attend qu'il finisse
      // vraiment de se fermer avant d'ouvrir le tiroir. Avoir deux dialogs modaux
      // simultanément peut casser la gestion du focus/top-layer du navigateur et
      // rendre le tiroir insensible aux clics (bouton fermer inclus) — ça
      // n'arrive jamais depuis un ajout classique sur la fiche produit, qui
      // n'ouvre aucune autre modale.
      const otherOpenDialog = /** @type {HTMLDialogElement | undefined} */ (
        Array.from(document.querySelectorAll('dialog[open]')).find(
          (dialog) => dialog !== this.refs.dialog && dialog.matches(':modal')
        )
      );

      if (otherOpenDialog) {
        let handled = false;
        const proceed = () => {
          if (handled) return;
          handled = true;
          otherOpenDialog.removeEventListener('close', proceed);
          this.#handleCartAdd(event);
        };

        otherOpenDialog.addEventListener('close', proceed, { once: true });
        // Safety net: never block the drawer indefinitely if that other dialog
        // doesn't close promptly (or at all) for an unrelated reason.
        setTimeout(proceed, 1000);
        return;
      }

      // Ajout réussi : explosion de confettis (déclenchée ici pour couvrir
      // tous les boutons "Ajouter au panier" du site, pas seulement un formulaire).
      // CartAddEvent and CartUpdateEvent share the same event name (both are
      // "cart:update" under the hood, so the drawer/cart-items sync logic can
      // react to either) — without this check, removing an item or changing
      // its quantity would also fire confetti. `instanceof` still tells them
      // apart since they're distinct classes.
      if (event instanceof CartAddEvent && !event?.detail?.data?.didError && typeof confetti === 'function') {
        if (this.refs.dialog?.open) {
          // Drawer already open (e.g. rapid successive add-to-cart clicks): fire right away.
          this.#launchConfettiInDrawer();
        } else {
          // showDialog() defers the actual `dialog.showModal()` call to the next animation
          // frame, so the drawer isn't open yet at this point. Wait for DialogOpenEvent
          // (dispatched right after showModal() succeeds) before measuring its position.
          this.addEventListener(DialogOpenEvent.eventName, this.#launchConfettiInDrawer, { once: true });
        }
      }

      this.showDialog();
    }
  };

  /**
   * Fires a confetti burst positioned over the cart drawer itself.
   *
   * Two constraints rule out the "obvious" implementations:
   * 1. The drawer is a native <dialog> shown via showModal(), which renders in
   *    the browser's top layer. A confetti canvas appended to document.body
   *    (the library's default behavior) renders BELOW that top layer, i.e.
   *    visually "behind" the drawer instead of inside it.
   * 2. Every successful add-to-cart also re-syncs the drawer's markup (a DOM
   *    "morph" against freshly rendered section HTML). Anything appended
   *    directly inside the dialog/section gets pruned by that sync almost
   *    immediately, since it doesn't exist in the server-rendered HTML.
   *
   * Fix: render into a dedicated overlay that lives outside the themed
   * section entirely (so the morph never touches it), and promote it into
   * the browser's top layer ourselves via the Popover API (same mechanism
   * the theme already relies on elsewhere, see popover-polyfill.js) so it
   * stacks above the already-open dialog. It's repositioned to match the
   * drawer's exact on-screen bounds every time it fires, so it tracks the
   * drawer correctly whether it's a full-width mobile panel or a desktop
   * sidebar.
   *
   * A third constraint this also has to account for: `showDialog()` defers
   * `dialog.showModal()` to the next animation frame, and the drawer then
   * slides into place via a CSS opening animation. Measuring the dialog's
   * position too early (before showModal() ran, or mid-slide) would produce
   * a wrong or zero-sized rect. This is called either once the dialog is
   * already open, or via a one-time DialogOpenEvent listener — and it always
   * waits for any in-flight animation on the dialog to finish before
   * measuring, so the rect reflects its final, settled position.
   */
  #launchConfettiInDrawer = () => {
    const dialog = this.refs.dialog;
    if (!dialog) return;

    onAnimationEnd(dialog, () => {
      let overlay = document.getElementById('confetti-drawer-overlay');
      if (!overlay) {
        overlay = document.createElement('div');
        overlay.id = 'confetti-drawer-overlay';
        overlay.setAttribute('popover', 'manual');
        overlay.style.margin = '0';
        overlay.style.padding = '0';
        overlay.style.border = 'none';
        overlay.style.background = 'transparent';
        overlay.style.pointerEvents = 'none';
        overlay.style.overflow = 'hidden';

        const canvas = document.createElement('canvas');
        canvas.style.width = '100%';
        canvas.style.height = '100%';
        canvas.style.display = 'block';
        canvas.style.pointerEvents = 'none';
        overlay.appendChild(canvas);

        document.body.appendChild(overlay);
      }

      const canvas = /** @type {HTMLCanvasElement} */ (overlay.querySelector('canvas'));
      const rect = dialog.getBoundingClientRect();
      // Never cover the header row: keeps the close button reachable even if a
      // browser mishandles pointer-events on a Popover-API top-layer element
      // stacked above an already-open <dialog>.
      const header = dialog.querySelector('.cart-drawer__header');
      const top = header ? header.getBoundingClientRect().bottom : rect.top;

      overlay.style.position = 'fixed';
      overlay.style.top = `${top}px`;
      overlay.style.left = `${rect.left}px`;
      overlay.style.width = `${rect.width}px`;
      overlay.style.height = `${Math.max(0, rect.bottom - top)}px`;
      overlay.style.zIndex = '2147483647';

      try {
        overlay.showPopover();
      } catch (error) {
        // Already open, or Popover API unsupported (polyfilled elsewhere in the theme).
      }

      // Origin the burst from wherever the visitor actually pressed "Ajouter au
      // panier" (projected onto — and clamped to — the drawer's own bounds, since
      // the button itself is usually outside the drawer's clipped canvas), then
      // fan it wide so it propagates across the whole drawer instead of staying
      // in one spot.
      const clamp = (/** @type {number} */ value) => Math.min(1, Math.max(0, value));
      const pointer = this.#lastPointerPosition;
      const origin = pointer
        ? {
            x: clamp((pointer.x - rect.left) / rect.width),
            y: clamp((pointer.y - rect.top) / rect.height),
          }
        : { x: 0.5, y: 0.3 };

      const drawerConfetti = confetti.create(canvas, { resize: true });
      drawerConfetti({
        particleCount: 160,
        spread: 160,
        startVelocity: 45,
        origin,
        colors: ['#1A1A1A', '#D4AF37', '#EBE7E1', '#FFFFFF'],
      });

      clearTimeout(this.#confettiHideTimeout);
      this.#confettiHideTimeout = /** @type {number} */ (
        /** @type {unknown} */ (
          setTimeout(() => {
            try {
              overlay.hidePopover();
            } catch (error) {
              // Already closed.
            }
          }, 3000)
        )
      );
    });
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
